import { useState, useCallback, useEffect, useMemo } from 'react';
import { cn } from '@/lib/infra/utils';
import type { Sport, MarketType, DateFilter, StatusFilter, MarketSide, PriceMode, TeamSide } from './types';

function todayMdy(): string {
  const d = new Date();
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
}

function mdyToIso(mdy: string): string {
  const [mm, dd, yyyy] = mdy.split('/');
  return `${yyyy}-${mm}-${dd}`;
}

function shiftMdy(mdy: string, days: number): string {
  const [mm, dd, yyyy] = mdy.split('/').map(Number);
  const d = new Date(yyyy, mm - 1, dd);
  d.setDate(d.getDate() + days);
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
}
import { DashboardToolbar } from './DashboardToolbar';
import { GameTable } from './GameTable';
import { SeriesTable } from './SeriesTable';
import { GameDetailPanel, type TradeIntent } from './GameDetailPanel';
import { TradeModal, type TradeModalData } from './TradeModal';
import { bestPriceCentsForOrderSide } from './tradeModalBook';
import { FeedTape } from './FeedTape';
import { PositionSidebar, PositionsWidget } from './PositionSidebar';
import { useMarketStore } from '../../stores/marketStore';
import { useDisplaySettings } from '../../stores/displaySettings';
import { useUserKalshiPOS } from '@/hooks/useUserKalshiPOS';

interface ValueDashboardProps {
  /** Lets the floating positions popup open the full Position Tracker tab. */
  onOpenPositionsPage?: () => void;
}

export function ValueDashboard({ onOpenPositionsPage }: ValueDashboardProps = {}) {
  // ── Hydra data (connection managed at App level) ───────────────────
  const connected = useMarketStore((s) => s.connected);
  const marketsCount = useMarketStore((s) => s.marketsCount);
  const orderbooksCount = useMarketStore((s) => s.orderbooksCount);
  const getGames = useMarketStore((s) => s.getGames);
  const getSeries = useMarketStore((s) => s.getSeries);
  const version = useMarketStore((s) => s.version);

  const feeAdjustedOdds = useDisplaySettings((s) => s.feeAdjustedOdds);
  const toggleFeeAdjustedOdds = useDisplaySettings((s) => s.toggleFeeAdjustedOdds);

  // ── Toolbar state ──────────────────────────────────────────────────
  const [selectedSport, setSelectedSport] = useState<Sport>('nba');
  const [selectedMarketType, setSelectedMarketType] = useState<MarketType>('moneyline');
  const [selectedDate, setSelectedDate] = useState(todayMdy);
  const [dateFilter, setDateFilter] = useState<DateFilter>('today');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [marketSide, setMarketSide] = useState<MarketSide>('no');
  const [priceMode, setPriceMode] = useState<PriceMode>('maker');

  // ── Feed tape state ─────────────────────────────────────────────────
  const [showFeedTape, setShowFeedTape] = useState(false);

  // ── Position sidebar state ─────────────────────────────────────────
  const [showPositionSidebar, setShowPositionSidebar] = useState(false);

  // ── Data from Hydra via Zustand ─────────────────────────────────────
  // version changes on every RAF tick → forces fresh derivation from mutable maps.
  // Series rows merge cross-venue via canonicalSeriesKey inside marketStore;
  // no client-side merge needed.
  const games = useMemo(() => {
    void version; // explicit dependency
    if (selectedMarketType === 'series') return [];
    return getGames(selectedSport, selectedMarketType, {
      statusFilter,
      dateFilter,
      selectedDateIso: mdyToIso(selectedDate),
    });
  }, [getGames, selectedSport, selectedMarketType, version, statusFilter, dateFilter, selectedDate]);

  const seriesEvent = useMemo(() => {
    void version;
    if (selectedMarketType !== 'series') return null;
    return getSeries(selectedSport);
  }, [getSeries, selectedSport, selectedMarketType, version]);

  // ── Orderbook needs ────────────────────────────────────────────────
  // Declare the set of tickers this view currently renders depth for.
  // The store unions these with other views' needs and hands the union
  // to HydraClient, which narrows the upstream orderbooks subscription.
  // Recompute from `games` (and the series outcomes when that tab's
  // active); `version` already triggers that memo upstream.
  const orderbookTickers = useMemo(() => {
    const set = new Set<string>();
    for (const g of games) {
      if (g.kalshi?.away.marketTicker) set.add(g.kalshi.away.marketTicker);
      if (g.kalshi?.home.marketTicker) set.add(g.kalshi.home.marketTicker);
      if (g.polymarket?.away.marketTicker) set.add(g.polymarket.away.marketTicker);
      if (g.polymarket?.home.marketTicker) set.add(g.polymarket.home.marketTicker);
    }
    if (seriesEvent) {
      for (const outcome of seriesEvent.outcomes) {
        if (outcome.kalshi?.marketTicker) set.add(outcome.kalshi.marketTicker);
      }
    }
    return Array.from(set);
  }, [games, seriesEvent]);

  // Stable key derived from the ticker set so the effect only re-runs when
  // the actual set changes, not on every RAF tick (orderbookTickers gets a
  // fresh array ref every tick because `games` does).
  const orderbookTickersKey = useMemo(
    () => orderbookTickers.slice().sort().join('|'),
    [orderbookTickers],
  );

  // Kalshi-only ticker subset for the per-user POS hook. We only need
  // queue-position lookups against tickers actually shown on screen — feeding
  // the full set keeps the queue-positions REST call narrow and avoids
  // pulling depth for off-screen orders (e.g. NHL while NBA is selected).
  const kalshiVisibleTickers = useMemo(() => {
    const set = new Set<string>();
    for (const g of games) {
      if (g.kalshi?.away.marketTicker) set.add(g.kalshi.away.marketTicker);
      if (g.kalshi?.home.marketTicker) set.add(g.kalshi.home.marketTicker);
    }
    return Array.from(set);
  }, [games]);
  const userActivity = useUserKalshiPOS(kalshiVisibleTickers);
  useEffect(() => {
    useMarketStore.getState().setOrderbookNeeds('value-dashboard', orderbookTickers);
    return () => {
      useMarketStore.getState().setOrderbookNeeds('value-dashboard', []);
    };
    // orderbookTickers is intentionally excluded — orderbookTickersKey is the
    // stable identity for the set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderbookTickersKey]);

  // ── Detail panel state ──────────────────────────────────────────────
  const [expandedGameKey, setExpandedGameKey] = useState<string | null>(null);
  const [tradeIntent, setTradeIntent] = useState<TradeIntent | null>(null);

  // ── Trade modal state (opens on odds cell click) ───────────────────
  const [tradeModal, setTradeModal] = useState<TradeModalData | null>(null);

  const handleToggleExpand = useCallback(
    (key: string, side: TeamSide) => {
      if (expandedGameKey === key) {
        if (tradeIntent?.side === side) {
          setExpandedGameKey(null);
          setTradeIntent(null);
        } else {
          setTradeIntent({ side, venue: 'kalshi' });
        }
        return;
      }
      setExpandedGameKey(key);
      setTradeIntent({ side, venue: 'kalshi' });
    },
    [expandedGameKey, tradeIntent?.side],
  );

  const handleCloseDetail = useCallback(() => {
    setExpandedGameKey(null);
    setTradeIntent(null);
  }, []);

  const handleTradeClick = useCallback(
    (
      gameKey: string,
      side: 'away' | 'home',
      venue: 'kalshi' | 'polymarket',
      opts?: { priceCents?: number; initialOrderSide?: 'yes' | 'no'; postOnly?: boolean },
    ) => {
      const game = games.find((g) => g.key === gameKey);
      if (!game) return;

      const initialOrderSide = opts?.initialOrderSide ?? marketSide;
      const book =
        side === 'away'
          ? (venue === 'kalshi' ? game.kalshi?.away : game.polymarket?.away)
          : (venue === 'kalshi' ? game.kalshi?.home : game.polymarket?.home);
      const prefillPrice =
        opts?.priceCents ?? bestPriceCentsForOrderSide(book, initialOrderSide);

      setTradeModal({
        game,
        teamSide: side,
        venue,
        prefillPrice,
        initialOrderSide,
        postOnly: opts?.postOnly ?? false,
      });

      setExpandedGameKey(gameKey);
      setTradeIntent({ side, venue });
    },
    [games, marketSide],
  );

  const expandedGame = useMemo(() => {
    if (!expandedGameKey) return null;
    return games.find((g) => g.key === expandedGameKey) ?? null;
  }, [expandedGameKey, games]);

  const seriesTicker = useMemo(() => {
    const map: Record<string, string> = {
      nba: 'KXNBAGAME',
      nhl: 'KXNHLGAME',
      cbb: 'KXNCAAMBGAME',
      atp: 'KXATPMATCH',
      wta: 'KXWTAMATCH',
      mlb: 'KXMLBGAME',
      ufc: 'KXUFCFIGHT',
    };
    return map[selectedSport] ?? '';
  }, [selectedSport]);

  // ── Date navigation ─────────────────────────────────────────────────
  // The day prev/next buttons always force single-day mode; navigating a
  // date while filtered to "all" is meaningless (the filter ignores the
  // date entirely).
  const handleDatePrev = useCallback(() => {
    setSelectedDate((d) => shiftMdy(d, -1));
    setDateFilter('today');
  }, []);

  const handleDateNext = useCallback(() => {
    setSelectedDate((d) => shiftMdy(d, 1));
    setDateFilter('today');
  }, []);

  // "Today" jumps the calendar back to the actual current day AND
  // enforces single-day mode, regardless of where the user has
  // navigated.
  const handleTodayClick = useCallback(() => {
    setSelectedDate(todayMdy());
    setDateFilter('today');
  }, []);

  return (
    <div className="flex h-full flex-col">
      {/* Toolbar row — DashboardToolbar grows to fill the table column;
          PositionsWidget is a fixed w-96 container occupying exactly the
          right column above the order panel (same width, same left
          divider, same right edge). */}
      <div className="border-border flex items-stretch border-b">
        <div className="min-w-0 flex-1 p-3">
          <DashboardToolbar
            selectedSport={selectedSport}
            selectedMarketType={selectedMarketType}
            selectedDate={selectedDate}
            dateFilter={dateFilter}
            statusFilter={statusFilter}
            marketSide={marketSide}
            priceMode={priceMode}
            onSportChange={setSelectedSport}
            onMarketTypeChange={setSelectedMarketType}
            onDateChange={setSelectedDate}
            onDatePrev={handleDatePrev}
            onDateNext={handleDateNext}
            onDateFilterChange={setDateFilter}
            onTodayClick={handleTodayClick}
            onStatusFilterChange={setStatusFilter}
            onMarketSideChange={setMarketSide}
            onPriceModeChange={setPriceMode}
          />
        </div>
        <div className="border-border w-96 shrink-0 border-l">
          <PositionsWidget
            expanded={showPositionSidebar}
            onToggle={() => setShowPositionSidebar((v) => !v)}
          />
        </div>
      </div>

      {/* Content row — game table + detail panel side-by-side, both below toolbar */}
      <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 overflow-auto p-4">
          {selectedMarketType === 'series' ? (
            seriesEvent ? (
              <SeriesTable event={seriesEvent} />
            ) : (
              <div className="text-muted-foreground flex h-64 flex-col items-center justify-center gap-2">
                {connected ? (
                  <span>No {selectedSport.toUpperCase()} series markets</span>
                ) : (
                  <>
                    <div className="border-border border-t-primary h-5 w-5 animate-spin rounded-full border-2" />
                    <span>Connecting to Hydra...</span>
                  </>
                )}
              </div>
            )
          ) : games.length > 0 ? (
            <GameTable
              games={games}
              seriesTicker={seriesTicker}
              marketType={selectedMarketType}
              marketSide={marketSide}
              priceMode={priceMode}
              expandedGameKey={expandedGameKey}
              expandedTeamSide={tradeIntent?.side ?? null}
              onToggleExpand={handleToggleExpand}
              onTradeClick={handleTradeClick}
              positionByTicker={userActivity.positionByTicker}
              ordersByTicker={userActivity.ordersByTicker}
            />
          ) : (
            <div className="text-muted-foreground flex h-64 flex-col items-center justify-center gap-2">
              {connected ? (
                <span>No {selectedSport.toUpperCase()} games</span>
              ) : (
                <>
                  <div className="border-border border-t-primary h-5 w-5 animate-spin rounded-full border-2" />
                  <span>Connecting to Hydra...</span>
                </>
              )}
            </div>
          )}
        </div>

        {/* Detail panel — sibling of the table, both below the toolbar. */}
        {expandedGame && (
          <GameDetailPanel
            key={expandedGameKey}
            game={expandedGame}
            onClose={handleCloseDetail}
            tradeIntent={tradeIntent}
            marketSide={marketSide}
            onTeamSideChange={(side) => {
              setTradeIntent((t) => (t ? { ...t, side } : null));
            }}
          />
        )}
      </div>

      {/* Bottom bar — odds-fee toggle (left), connection + feed tape (right) */}
      <div className="border-border flex items-center justify-between border-t px-3 py-1.5">
        {/* Fees / Raw odds toggle. Default: Fees on (maker rate), since the
            typical workflow is bidding (maker). Click to compare against raw. */}
        <button
          onClick={toggleFeeAdjustedOdds}
          title={
            feeAdjustedOdds
              ? 'Showing fee-adjusted (maker) odds — click for raw'
              : 'Showing raw odds — click for fee-adjusted (maker)'
          }
          className={cn(
            'rounded-md border px-2 py-1 font-mono text-[11px] transition-colors',
            feeAdjustedOdds
              ? 'border-primary text-primary'
              : 'border-border text-muted-foreground hover:text-foreground',
          )}
        >
          {feeAdjustedOdds ? 'Fees' : 'Raw'}
        </button>

        <div className="relative">
          <button
            onClick={() => setShowFeedTape((v) => !v)}
            className="flex w-44 items-center gap-2 rounded-md px-2 py-1 text-xs transition-colors hover:bg-accent"
          >
            <span className={`inline-block h-2 w-2 shrink-0 rounded-full ${connected ? 'bg-positive animate-pulse' : 'bg-negative'}`} />
            <span className="text-muted-foreground truncate">
              {connected ? `${marketsCount} mkts / ${orderbooksCount} books` : 'Disconnected'}
            </span>
          </button>
          {showFeedTape && <FeedTape onClose={() => setShowFeedTape(false)} />}
        </div>
      </div>

      {/* Position popup — fixed-position overlay, can coexist with the
          detail panel (used to fight it for the right rail). */}
      {showPositionSidebar && (
        <PositionSidebar
          onClose={() => setShowPositionSidebar(false)}
          onOpenDetail={
            onOpenPositionsPage
              ? () => {
                  setShowPositionSidebar(false);
                  onOpenPositionsPage();
                }
              : undefined
          }
        />
      )}

      {/* Trade modal (overlays on odds cell click) */}
      {tradeModal && (
        <TradeModal
          key={`${tradeModal.game.key}-${tradeModal.teamSide}-${tradeModal.venue}-${tradeModal.prefillPrice ?? ''}-${tradeModal.initialOrderSide ?? 'no'}-${tradeModal.postOnly ? 'm' : 't'}`}
          data={tradeModal}
          onClose={() => setTradeModal(null)}
          onOrderPlaced={userActivity.refresh}
        />
      )}
    </div>
  );
}
